import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

export interface DocumentBrandingFormState {
  logoDataUrl: string;
  headerText: string;
  footerText: string;
  showLogo: boolean;
  showHeader: boolean;
  showFooter: boolean;
  showPageNumbers: boolean;
  logoWidthMm: number;
  marginTopMm: number;
  marginRightMm: number;
  marginBottomMm: number;
  marginLeftMm: number;
}

export const createDefaultDocumentBranding =
  (): DocumentBrandingFormState => ({
    logoDataUrl: '',
    headerText: '',
    footerText: '',
    showLogo: true,
    showHeader: true,
    showFooter: true,
    showPageNumbers: true,
    logoWidthMm: 28,
    marginTopMm: 25,
    marginRightMm: 20,
    marginBottomMm: 20,
    marginLeftMm: 30,
  });

type BrandingResponse = {
  success?: boolean;
  error?: string;

  documentBranding?: Partial<
    DocumentBrandingFormState
  > | null;
};

const normalizeBranding = (
  value:
    Partial<DocumentBrandingFormState> |
    null |
    undefined
): DocumentBrandingFormState => {
  const defaults =
    createDefaultDocumentBranding();

  if (!value) {
    return defaults;
  }

  return {
    logoDataUrl:
      String(
        value.logoDataUrl ?? ''
      ),

    headerText:
      String(
        value.headerText ?? ''
      ),

    footerText:
      String(
        value.footerText ?? ''
      ),

    showLogo:
      value.showLogo ??
      defaults.showLogo,

    showHeader:
      value.showHeader ??
      defaults.showHeader,

    showFooter:
      value.showFooter ??
      defaults.showFooter,

    showPageNumbers:
      value.showPageNumbers ??
      defaults.showPageNumbers,

    logoWidthMm:
      Number(
        value.logoWidthMm ??
        defaults.logoWidthMm
      ),

    marginTopMm:
      Number(
        value.marginTopMm ??
        defaults.marginTopMm
      ),

    marginRightMm:
      Number(
        value.marginRightMm ??
        defaults.marginRightMm
      ),

    marginBottomMm:
      Number(
        value.marginBottomMm ??
        defaults.marginBottomMm
      ),

    marginLeftMm:
      Number(
        value.marginLeftMm ??
        defaults.marginLeftMm
      ),
  };
};

export function useDocumentBranding() {
  const [
    documentBranding,
    setDocumentBranding,
  ] =
    useState<DocumentBrandingFormState>(
      createDefaultDocumentBranding()
    );

  const [
    isLoadingDocumentBranding,
    setIsLoadingDocumentBranding,
  ] = useState(true);

  const [
    isSavingDocumentBranding,
    setIsSavingDocumentBranding,
  ] = useState(false);

  const loadDocumentBranding =
    useCallback(
      async () => {
        setIsLoadingDocumentBranding(
          true
        );

        try {
          const response =
            await fetchWithRetry(
              '/api/document-branding'
            );

          const data =
            await response.json() as
              BrandingResponse;

          if (
            !response.ok ||
            !data.success
          ) {
            throw new Error(
              data.error ||
              'Falha ao carregar identidade documental.'
            );
          }

          const normalized =
            normalizeBranding(
              data.documentBranding
            );

          setDocumentBranding(
            normalized
          );

          return normalized;
        } finally {
          setIsLoadingDocumentBranding(
            false
          );
        }
      },
      []
    );

  const saveDocumentBranding =
    useCallback(
      async (
        input:
          DocumentBrandingFormState
      ) => {
        setIsSavingDocumentBranding(
          true
        );

        try {
          const response =
            await fetchWithRetry(
              '/api/document-branding',
              {
                method: 'PUT',

                headers: {
                  'Content-Type':
                    'application/json',
                },

                body:
                  JSON.stringify(
                    input
                  ),
              }
            );

          const data =
            await response.json() as
              BrandingResponse;

          if (
            !response.ok ||
            !data.success
          ) {
            throw new Error(
              data.error ||
              'Falha ao salvar identidade documental.'
            );
          }

          const normalized =
            normalizeBranding(
              data.documentBranding
            );

          setDocumentBranding(
            normalized
          );

          return normalized;
        } finally {
          setIsSavingDocumentBranding(
            false
          );
        }
      },
      []
    );

  useEffect(
    () => {
      void loadDocumentBranding();
    },
    [loadDocumentBranding]
  );

  return {
    documentBranding,
    setDocumentBranding,

    isLoadingDocumentBranding,
    isSavingDocumentBranding,

    loadDocumentBranding,
    saveDocumentBranding,
  };
}